/**
 * ArCell Component
 *
 * Atomic rendering unit for a single cell in ArTable.
 * Subscribes to its own cell data for granular reactivity.
 * Owns all cell-level concerns: value, locked state, styling, persistence.
 * Delegates rendering to ArCellRouter.
 *
 * **Derive-projected cells:** `cell._calculated` marks a WHOLE cell the Form
 * Derive reconciler owns. It disables the control AND makes `buildUpdatesForRow`
 * skip the row, so a bulk fan-out cannot write over a projection either. It also
 * joins `isFormulaColumn` in the `isCalculated` prop, which is the calculated
 * *treatment* — grey fill, italic, not focusable — not the authority: the
 * authority stays per cell, because a projection owns some rows of a target
 * column while the manual rows beside them stay editable.
 *
 * A projection may instead own ONE composite sub-field of a cell, and then it
 * writes no marker at all — the sub-fields beside it are still the person's. Those
 * keys arrive as `derivedSubFields`, join the column's formula targets in
 * `ownedSubFields`, and are refused on write and rendered disabled inside the
 * composite sidebar. Never `_calculated` on the parent: that would lock the
 * siblings too. `derivedCellTooltip` is the hover line for the cell, hung on the
 * presence of a projection rather than on the marker, so a dotted column shows it;
 * `derivedSubFields` maps each locked key to that same sentence, which the sidebar
 * hangs on the control it disables.
 *
 * **Valtio reactivity:**
 * Subscribes to the row's `cells` map with `{ sync: true }`, accessing only `cells[columnId]`.
 * Valtio tracks per-key access — only this cell's mutations trigger re-render, and
 * reading an absent key still registers it, so the cell re-renders once it appears.
 * The render body NEVER writes the row proxy: a write there notifies every other
 * subscriber mid-render. An absent cell materializes lazily, from
 * `resolveCellValueProxy`, at the moment the sidebar opens.
 * Row existence is guaranteed by ArRow.
 *
 * **Undo (SYD-2297):**
 * Every path `buildUpdatesForRow` writes is paired with the value it held just
 * before the mutation, and one user action produces exactly one
 * `formUndoStack.record` call — a bulk edit records all its rows together, so a
 * single ⌘Z reverts the whole fan-out. The target key is the rows collection
 * path, which `ArTableBody` registers its adapter under. Formula columns are
 * never captured: nothing writes them from here.
 *
 * **Bulk edit scope (SYD-2386):** the fan-out, the preview highlight and the
 * confirmation count all run off `resolveSelectionScope`'s
 * `selectedVisibleRowIds`, so a row ticked before a split, column filter or
 * visibility view hid it is never written. A cell edit is a visible-rows action
 * whatever the selection scope says — the whole-table actions live in the
 * Actions menu, and nothing here could show the user what it wrote.
 *
 * **Formula display channel:** formula columns and compound formula sub-fields
 * read `formulaResults_<typeId>` off the row ENTRY snapshot first — the derive
 * writes those keys outside `rowEntry.data`, beyond mergeDocumentIntoProxy's
 * mirror-delete — and fall back to the persisted `value` / `fields[sub].value`
 * only when no derive owns the key (bundle headers, suppressFormulas rows,
 * pre-backfill rows). Never to `calcInput`: on exactly those rows the engine
 * declines to compute, `calcInput` is an orphan no writer owns, while `value` is
 * the writer's own. The keys hold `number | null`, never `undefined`, so a
 * `!== undefined` read is key presence: a derived `null` blanks the cell
 * instead of resurrecting a stale persisted number. Only these leaf keys are
 * read off the entry snapshot, keeping the subscription per-key.
 */

import { FC, memo, useCallback, useContext, useMemo, CSSProperties } from 'react';
import { theme } from 'antd';
import get from 'lodash/get';
import { usePdfExportMode } from '../../usePdfExportMode';
import { useVisibilityMode } from '@/use/useVisibilityMode';
import { useSnapshot } from 'valtio';
import { MessageContext } from '@/providers/MessageContext';
import { useStoreCollection, useStoreNonReactive, type DocEntry, type DocumentState } from '@/store';
import { formUndoStack } from '@/store/form/formUndoStack';
import type { UndoChange } from '@/store/form/formUndoStack';
import { isCompoundSubFieldChangePayload, isCompoundCellChangePayload } from '#types';
import type {
  ArBundleType,
  ArCell as ArCellType,
  ArCellValue,
  ArRowId,
  ArRowData,
  ArColumnDefinition,
  ArColumnReference,
  ArEntityReference,
  ArTableType,
  TypeId,
} from '#types';
import { UIElementType } from '#types/System.types';
import { BULK_CONFIRM_THRESHOLD } from '#types';
import type { EntityRecord, ReferencedDocument } from '#types';

import ArCellRouter from './ArCellRouter';
import { isBundleMemberCellSuppressed } from './isBundleMemberCellSuppressed';
import { ArTableUIStateContext } from '../../context';
import { resolveSelectionScope } from '../../selection';

/** Stands in for a cell absent from the row's `cells` map. Frozen and module-level so its identity never moves between renders. */
const EMPTY_CELL: ArCellType = Object.freeze({ value: null });

type ArCellProps = {
  column: ArColumnDefinition;
  rowId: ArRowId;
  disabled?: boolean;
  columnIndex: number;
  stickyFirstColumn?: boolean;
  frozenLeftCount?: number;
  frozenLeftOffsetsByIndex?: number[];
  isOddRow?: boolean;
  isRowLocked?: boolean;
  isColumnProtected?: boolean;
  isFormulaColumn?: boolean;
  formulaSubFields?: ReadonlySet<string>;
  /**
   * The projected-cell hover line, handed down by `ArRow` for the one column a
   * Form Derive projection targets on this row. Present exactly when a projection
   * fills this column on this row, which is the whole condition: a dotted column
   * writes no `_calculated` marker, so gating the line on that marker hid it from
   * every composite sub-field a derive fills.
   */
  derivedCellTooltip?: string;
  /**
   * The composite sub-fields a Form Derive projection fills inside this cell,
   * handed down by `ArRow`, each mapped to the sentence saying why it is locked —
   * the same sentence `derivedCellTooltip` carries, so the cell and the sidebar
   * word one derive one way. Refused on write and rendered disabled, the same
   * treatment `formulaSubFields` gets — and for the same reason: the derive owns
   * these keys of the cell while the ones beside them stay the person's.
   */
  derivedSubFields?: ReadonlyMap<string, string>;
  bundleRole?: 'member';
  bundleType?: ArBundleType;
  /** Card mode (below `md`): the cell spans the card's full width beneath its label, and draws no column rule. */
  cardMode?: boolean;
};

/**
 * ArCell manages cell state and persistence.
 * Ensures its cell proxy exists, subscribes directly to it for granular reactivity.
 * Computes its own disabled state from row disabled + cell.locked + cell._calculated + isColumnProtected.
 * Delegates rendering to ArCellRouter based on column type.
 */
const ArCell: FC<ArCellProps> = ({
  column,
  rowId,
  disabled = false,
  columnIndex,
  stickyFirstColumn = true,
  frozenLeftCount = 0,
  frozenLeftOffsetsByIndex = [],
  isOddRow = false,
  isRowLocked = false,
  isColumnProtected = false,
  isFormulaColumn = false,
  formulaSubFields,
  derivedCellTooltip,
  derivedSubFields,
  bundleRole,
  bundleType,
  cardMode = false,
}) => {
  const { typeId: columnId } = column;
  const visibilityMode = useVisibilityMode();
  const { token } = theme.useToken();
  const { handleFirestoreWriteError } = useContext(MessageContext);
  const uiState = useContext(ArTableUIStateContext)!;
  const pdfExportMode = usePdfExportMode();
  const cellId = `artable-cell-${columnId}-${rowId}`;

  const cellEditingSnap = useSnapshot(uiState.cellEditing, { sync: true });
  const isEditing = (cellEditingSnap.editingCellsByKey[cellId] ?? false) === true;
  const { state: rowsState, persist } = useStoreCollection<ArRowData>('rows');
  const { state: schemaStateRaw } = useStoreNonReactive('schema');
  const schemaState = schemaStateRaw as DocumentState<ArTableType>;
  const rowEntry = rowsState.docs.get(rowId)!;
  const rowCells = rowEntry.data!.cells;
  const cellsSnap = useSnapshot(rowCells, { sync: true });
  const cell = (cellsSnap[columnId] ?? EMPTY_CELL) as ArCellType;

  const entrySnap = useSnapshot(
    rowEntry as DocEntry<ArRowData> & Record<string, unknown>,
    { sync: true },
  ) as Record<string, unknown>;
  const formulaResultKey = `formulaResults_${columnId}`;
  const derivedResult = entrySnap[formulaResultKey];
  const derivedSubResults = [...(formulaSubFields ?? [])].map(
    (sub) => [sub, entrySnap[`${formulaResultKey}.${sub}`]] as const,
  );

  const directValue: ArCellValue =
    isFormulaColumn && derivedResult !== undefined
      ? (typeof derivedResult === 'number' ? derivedResult : null)
      : (cell.value ?? null);

  const derivedSubFingerprint = derivedSubResults
    .map(([sub, derived]) => `${sub}=${derived === undefined ? '' : String(derived)}`)
    .join('\n');
  const value = useMemo((): ArCellValue => {
    if (!derivedSubResults.some(([, derived]) => derived !== undefined)) return directValue;
    const base =
      directValue && typeof directValue === 'object'
        ? (directValue as Record<string, unknown>)
        : {};
    const baseFields =
      base.fields && typeof base.fields === 'object'
        ? (base.fields as Record<string, unknown>)
        : {};
    const fields: Record<string, unknown> = { ...baseFields };
    for (const [sub, derived] of derivedSubResults) {
      if (derived === undefined) continue;
      const existing = fields[sub];
      fields[sub] = {
        ...(existing && typeof existing === 'object' ? (existing as Record<string, unknown>) : {}),
        value: typeof derived === 'number' ? derived : null,
      };
    }
    return { ...base, fields } as ArCellValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- derivedSubResults content is pinned by derivedSubFingerprint
  }, [directValue, derivedSubFingerprint]);

  const cellLocked = cell.locked ?? false;
  /**
   * The per-cell server-owned marker, written by `reconcileFormDerives` onto the
   * rows a projection owns. Formula columns lock through the column-level
   * `isFormulaColumn` channel instead, which cannot serve derives: a projection
   * owns SOME rows of a target column and the manual rows beside them stay
   * editable, so the lock has to be per cell.
   */
  const cellCalculated = cell._calculated === true;
  /**
   * Every sub-field of this cell a computed writer owns: the column's formula
   * targets and the sub-fields a Form Derive projection fills on this row. One
   * set, because the refusal is the same and a control cannot tell the two apart.
   * Both inputs arrive from a memoized map, so the identity holds across renders.
   */
  const ownedSubFields = useMemo(
    () => new Set<string>([...(formulaSubFields ?? []), ...(derivedSubFields?.keys() ?? [])]),
    [formulaSubFields, derivedSubFields],
  );
  // Note: isFormulaColumn is NOT included here; it's passed separately via isCalculated
  // ControlUI handles formula column behavior per field type (e.g., sellPriceUI allows partial editing)
  // For formula columns, editable===false is also excluded; isCalculated handles disabling non-sellPriceUI fields
  const disabledOrLocked = disabled || isRowLocked || cellLocked || cellCalculated || isColumnProtected;

  const handleValueChange = useCallback(async (newValue: ArCellValue, referencedDocument?: ReferencedDocument | null) => {
    // Guard: a pending bulk-edit confirmation gates all writes until the user answers the modal.
    // Covers commit-on-blur controls (e.g. SellPriceUI) whose blur, triggered when the modal
    // steals focus, would otherwise fan the focused value out to every selected row.
    if (uiState.bulkEditPreview.pendingConfirmation) return;
    // Guard: protected columns cannot be modified
    if (isColumnProtected) return;

    const recordForMapping = referencedDocument ?? null;

    const elementMapping = (() => {
      if (!recordForMapping) return null;
      if (column.uiElementType !== UIElementType.Reference) return null;
      if (!('referenceType' in column) || column.referenceType !== 'entity') return null;
      const mapping = (column as ArColumnReference).elementMapping;
      if (!mapping || mapping.length === 0) return null;
      return mapping;
    })();

    const schemaColumns = schemaState.data?.columns ?? [];
    const protectedTypeIdsFromSchema = new Set<TypeId>(schemaState.data?.protected?.protectedTypeIds ?? []);
    const formulaTypeIdsFromSchema = elementMapping
      ? new Set<TypeId>(schemaState.data?.formulaSequence?.map((e) => e.typeId) ?? [])
      : null;
    const schemaColumnByTypeId = elementMapping
      ? new Map<TypeId, ArColumnDefinition>(schemaColumns.map((c) => [c.typeId, c] as const))
      : null;

    /**
     * Builds Firestore updates for a single row and applies optimistic mutations.
     * Returns null if the row should be skipped (locked row/cell).
     */
    const buildUpdatesForRow = (
      docId: ArRowId,
      mutableRow: NonNullable<ReturnType<typeof rowsState.docs.get>>,
    ): { updates: Record<string, unknown>; changes: UndoChange[] } | null => {
      if (!mutableRow.data) return null;

      const rowLocked = (mutableRow.data as { locked?: boolean }).locked === true;
      const existingCell = mutableRow.data.cells?.[columnId] as ArCellType | undefined;
      const lockedCell = existingCell?.locked === true;
      /** A derive-projected cell is server-owned; a bulk fan-out must skip it too. */
      const calculatedCell = existingCell?._calculated === true;
      if (rowLocked || lockedCell || calculatedCell) return null;

      const updates: Record<string, unknown> = {};
      const changes: UndoChange[] = [];

      /**
       * Snapshots what `path` holds *right now*, so it must be called before the
       * mutation that overwrites it. An absent prior value becomes `null`:
       * `undefined` is not writable to Firestore, and `ArCell` materializes an
       * absent cell as `{ value: null }` anyway.
       */
      const capturePriorValue = (path: string, next: unknown): void => {
        changes.push({ docId, path, prev: get(mutableRow.data, path) ?? null, next });
      };

      if (!mutableRow.data.cells) {
        mutableRow.data.cells = {};
      }
      if (!mutableRow.data.cells[columnId]) {
        mutableRow.data.cells[columnId] = { value: null };
      }

      if (isCompoundSubFieldChangePayload(newValue)) {
        const { container, key } = newValue.subField;
        /**
         * The one key this payload carries belongs to a computed writer, so there
         * is nothing left to write and the row is skipped. A dotted projection
         * column marks no cell, which makes `ownedSubFields` the only thing
         * between the person's edit and the pass that would overwrite it.
         */
        if (container === 'fields' && ownedSubFields.has(key)) return null;
        const mutableCell = mutableRow.data.cells[columnId];

        if (!mutableCell.value || typeof mutableCell.value !== 'object') {
          mutableCell.value = {};
        }
        const mutableValue = mutableCell.value as Record<string, unknown>;

        if (!mutableValue[container] || typeof mutableValue[container] !== 'object') {
          mutableValue[container] = {};
        }
        capturePriorValue(`cells.${columnId}.value.${container}.${key}`, newValue.value);
        (mutableValue[container] as Record<string, unknown>)[key] = newValue.value;
        updates[`cells.${columnId}.value.${container}.${key}`] = newValue.value;
      } else if (isCompoundCellChangePayload(newValue)) {
        if (ownedSubFields.size > 0 && 'fields' in newValue && newValue.fields) {
          const mutableCell = mutableRow.data.cells[columnId];
          if (!mutableCell.value || typeof mutableCell.value !== 'object') {
            mutableCell.value = {};
          }
          const mutableValue = mutableCell.value as Record<string, Record<string, unknown>>;
          if (!mutableValue.fields || typeof mutableValue.fields !== 'object') {
            mutableValue.fields = {};
          }
          const fields = newValue.fields as Record<string, unknown>;
          for (const [key, fieldValue] of Object.entries(fields)) {
            if (ownedSubFields.has(key)) continue;
            capturePriorValue(`cells.${columnId}.value.fields.${key}`, fieldValue);
            mutableValue.fields[key] = fieldValue;
            updates[`cells.${columnId}.value.fields.${key}`] = fieldValue;
          }
        } else {
          capturePriorValue(`cells.${columnId}.value`, newValue);
          mutableRow.data.cells[columnId].value = newValue;
          updates[`cells.${columnId}.value`] = newValue;
        }
      } else {
        capturePriorValue(`cells.${columnId}.value`, newValue as ArCellValue);
        mutableRow.data.cells[columnId].value = newValue as ArCellValue;
        updates[`cells.${columnId}.value`] = newValue as ArCellValue;
      }

      // Element mapping only applies to entity references, which return EntityRecord
      const entityRecordForMapping = recordForMapping && 'fields' in recordForMapping ? recordForMapping as EntityRecord : null;

      if (elementMapping && schemaColumnByTypeId && entityRecordForMapping) {
        for (const mapping of elementMapping) {
          if (mapping.target.type !== 'column') continue;

          const targetTypeId = mapping.target.id as TypeId;
          if (!targetTypeId) continue;
          if (targetTypeId === columnId) continue;
          if (protectedTypeIdsFromSchema.has(targetTypeId)) continue;

          const targetColumn = schemaColumnByTypeId.get(targetTypeId);
          if (!targetColumn) continue;

          const isTargetFormulaColumn =
            targetColumn.uiElementType === UIElementType.Field &&
            formulaTypeIdsFromSchema?.has(targetTypeId);
          if (isTargetFormulaColumn) continue;

          const existingTargetCell = mutableRow.data.cells?.[targetTypeId] as ArCellType | undefined;
          if (existingTargetCell?.locked === true) continue;

          const rawValue = (() => {
            if (mapping.source.type === 'field') {
              return entityRecordForMapping.fields?.[mapping.source.id]?.value ?? null;
            }
            const ref = entityRecordForMapping.entityReferences?.[mapping.source.id];
            return ref?.entityRecordId ?? null;
          })();

          const mappedValue: ArCellValue = (() => {
            if (
              targetColumn.uiElementType === UIElementType.Reference &&
              'referenceType' in targetColumn &&
              targetColumn.referenceType === 'entity' &&
              typeof rawValue === 'string' &&
              rawValue.length > 0
            ) {
              const sourceRef = entityRecordForMapping.entityReferences?.[mapping.source.id];
              return {
                referenceType: 'entity',
                docId: rawValue,
                cacheLabel: sourceRef?.entityRecordName,
                ...('entityGroupId' in targetColumn && targetColumn.entityGroupId && { entityGroupId: targetColumn.entityGroupId }),
              } satisfies ArEntityReference;
            }

            return rawValue as ArCellValue;
          })();

          if (!mutableRow.data.cells[targetTypeId]) {
            mutableRow.data.cells[targetTypeId] = { value: null };
          }

          capturePriorValue(`cells.${targetTypeId}.value`, mappedValue);
          mutableRow.data.cells[targetTypeId].value = mappedValue;
          updates[`cells.${targetTypeId}.value`] = mappedValue;
        }
      }

      return { updates, changes };
    };

    const selection = uiState.selection;
    /**
     * The fan-out writes the SCOPED selection: a row ticked before a split,
     * filter or view hid it is not on screen to be edited (SYD-2386). Read off
     * the bare proxy at event time, which is when the write is decided.
     */
    const { selectedVisibleRowIds: bulkTargetRowIds } = resolveSelectionScope({
      selectableRowIds: uiState.selectableRowIds,
      selectedRows: selection.selectedRows,
      scope: selection.scope,
    });
    const shouldBulkUpdate = bulkTargetRowIds.includes(rowId) && bulkTargetRowIds.length > 1;

    if (!shouldBulkUpdate) {
      const mutableRow = rowsState.docs.get(rowId);
      if (!mutableRow) return;

      const built = buildUpdatesForRow(rowId, mutableRow);
      if (!built) return;

      formUndoStack.record(persist.collectionRef.path, built.changes);

      try {
        await persist.update(rowId, built.updates);
      } catch (error) {
        handleFirestoreWriteError(error, '[ArCell] update cell value', {
          userMessage: 'Failed to update cell value. Please try again.',
        });
      }

      return;
    }

    const persistPromises: Promise<void>[] = [];
    /** One entry for the whole fan-out, so a single ⌘Z reverts every row. */
    const bulkChanges: UndoChange[] = [];

    for (const targetRowId of bulkTargetRowIds) {
      const mutableRow = rowsState.docs.get(targetRowId);
      if (!mutableRow) continue;

      const built = buildUpdatesForRow(targetRowId, mutableRow);
      if (!built) continue;

      bulkChanges.push(...built.changes);
      persistPromises.push(persist.update(targetRowId, built.updates));
    }

    formUndoStack.record(persist.collectionRef.path, bulkChanges);

    const firstError =
      (await Promise.allSettled(persistPromises)).find((r) => r.status === 'rejected') as
        | PromiseRejectedResult
        | undefined;

    if (firstError) {
      handleFirestoreWriteError(firstError.reason, '[ArCell] update cell value', {
        userMessage: 'Failed to update some rows. Please try again.',
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- Valtio proxies (rowsState.docs, uiState.selection) are stable refs
  }, [column, columnId, ownedSubFields, handleFirestoreWriteError, isColumnProtected, persist, rowId, schemaState.data]);

  const handleFocus = useCallback(() => {
    if (!disabledOrLocked) {
      if (uiState.bulkEditPreview.pendingConfirmation) return;

      uiState.cellFocus.focusedCell = { rowId, columnId };

      const selection = uiState.selection;
      /**
       * Scoped exactly as the commit path is, so the preview highlight and the
       * confirmation count describe the rows the fan-out will actually write.
       */
      const { selectedVisibleRowIds: bulkTargetRowIds } = resolveSelectionScope({
        selectableRowIds: uiState.selectableRowIds,
        selectedRows: selection.selectedRows,
        scope: selection.scope,
      });
      const shouldBulkUpdate = bulkTargetRowIds.includes(rowId);

      if (!shouldBulkUpdate) {
        uiState.cellEditing.editingCellsByKey[cellId] = true;
        delete uiState.bulkEditPreview.byColumnId[columnId];
        return;
      }

      const targetRows: Record<ArRowId, true> = {};

      for (const targetRowId of bulkTargetRowIds) {
        const mutableRow = rowsState.docs.get(targetRowId);
        if (!mutableRow?.data) {
          continue;
        }

        const rowLocked = (mutableRow.data as any)?.locked === true;
        const existingCell = mutableRow.data.cells?.[columnId] as ArCellType | undefined;
        const lockedCell = existingCell?.locked === true;
        if (rowLocked || lockedCell || existingCell?._calculated === true) {
          continue;
        }

        targetRows[targetRowId] = true;
      }

      if (bulkTargetRowIds.length >= BULK_CONFIRM_THRESHOLD) {
        if (uiState.bulkEditPreview.byColumnId[columnId]) {
          uiState.cellEditing.editingCellsByKey[cellId] = true;
          return;
        }

        uiState.cellEditing.editingCellsByKey[cellId] = true;
        uiState.bulkEditPreview.pendingConfirmation = {
          columnId,
          rowId,
          targetRows,
        };
        return;
      }

      uiState.cellEditing.editingCellsByKey[cellId] = true;
      uiState.bulkEditPreview.byColumnId[columnId] = { sourceRowId: rowId, targetRows };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Valtio proxies (rowsState.docs, uiState.*) are stable refs
  }, [cellId, columnId, disabledOrLocked, rowId]);

  const handleBlur = useCallback(() => {
    const pending = uiState.bulkEditPreview.pendingConfirmation;
    if (pending?.rowId === rowId && pending?.columnId === columnId) return;

    delete uiState.cellEditing.editingCellsByKey[cellId];

    const previewForColumn = uiState.bulkEditPreview.byColumnId[columnId];
    if (previewForColumn?.sourceRowId === rowId) {
      delete uiState.bulkEditPreview.byColumnId[columnId];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Valtio proxies (uiState.*) are stable refs
  }, [cellId, columnId, rowId]);

  /**
   * Materializes this cell on the live row proxy and hands it back. Called only
   * from the handler that opens the composite-field sidebar — never during
   * render, where a write to a Firestore-backed proxy notifies other
   * subscribers mid-commit.
   */
  const resolveCellValueProxy = useCallback((): ArCellType => {
    const cells = rowsState.docs.get(rowId)!.data!.cells;
    if (!cells[columnId]) cells[columnId] = { value: null };
    return cells[columnId];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Valtio proxies (rowsState.docs) are stable refs
  }, [columnId, rowId]);

  const rowBackground = isOddRow ? token.colorBgBase : token.colorFillQuaternary;
  const bulkEditPreviewSnap = useSnapshot(uiState.bulkEditPreview);
  const isPreviewTarget =
    (bulkEditPreviewSnap.byColumnId[columnId]?.targetRows?.[rowId] ?? false) === true;
  const cellBackground = isPreviewTarget ? token.colorPrimaryBg : rowBackground;

  const cellStyle: CSSProperties = useMemo(() => {
    const style: CSSProperties = {
      height: pdfExportMode ? 'auto' : token.controlHeight,
      maxHeight: pdfExportMode ? undefined : token.controlHeight,
      minHeight: pdfExportMode ? undefined : token.controlHeight,
      display: 'flex',
      alignItems: pdfExportMode ? 'flex-start' : 'center',
      alignSelf: pdfExportMode ? 'stretch' : undefined,
      cursor: pdfExportMode ? 'default' : (disabledOrLocked ? 'not-allowed' : 'pointer'),
      borderRight: cardMode ? 'none' : `2px solid ${token.colorBorderSecondary}60`,
      gridColumnStart: cardMode ? 1 : undefined,
      gridColumnEnd: cardMode ? -1 : undefined,
      borderRadius: 0,
      backgroundColor: cellBackground,
      outline: isEditing ? `1px dashed ${token.colorPrimary}` : 'none',
      outlineOffset: -1,
      boxSizing: 'border-box',
      minWidth: 0,
      overflow: pdfExportMode ? 'visible' : 'hidden',
      width: '100%',
      flex: 1,
      overflowWrap: pdfExportMode ? 'anywhere' : undefined,
      wordBreak: pdfExportMode ? 'break-word' : undefined,
      whiteSpace: pdfExportMode ? 'normal' : undefined,
    };

    if (!pdfExportMode && stickyFirstColumn && columnIndex < frozenLeftCount) {
      const left = frozenLeftOffsetsByIndex[columnIndex];
      if (left === undefined) {
        return style;
      }

      style.position = 'sticky';
      style.left = left;
      style.zIndex = 20 + (frozenLeftCount - columnIndex);
      style.backdropFilter = 'blur(3px)';
      style.backgroundColor = `color-mix(in srgb, ${cellBackground} 56%, transparent)`;
    }

    return style;
  }, [
    cardMode,
    columnIndex,
    disabledOrLocked,
    isEditing,
    cellBackground,
    stickyFirstColumn,
    frozenLeftCount,
    frozenLeftOffsetsByIndex,
    token.colorBorderSecondary,
    token.colorPrimary,
    token.controlHeight,
    pdfExportMode,
  ]);

  if (isBundleMemberCellSuppressed(bundleRole, bundleType, visibilityMode, column)) {
    return <div role="cell" style={cellStyle} aria-label={cellId} />;
  }

  return (
    <ArCellRouter
      column={column}
      value={value}
      onChange={handleValueChange}
      disabled={disabledOrLocked}
      cellId={cellId}
      rowId={rowId}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={cellStyle}
      isCalculated={isFormulaColumn || cellCalculated}
      title={derivedCellTooltip}
      derivedSubFields={derivedSubFields}
      resolveCellValueProxy={resolveCellValueProxy}
    />
  );
};

export default memo(ArCell);
